import React, { useEffect, useState } from 'react';
import api from '../../lib/api';

/**
 * CodeDiffViewer — Phase 51.C Lab section.
 * Lists generated diffs and shows their content (full file or unified diff).
 * Read-only view. Application of diffs to live code happens in Phase 51.D
 * (PreviewBuilder).
 */
export default function CodeDiffViewer() {
  const [diffs, setDiffs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    api.get('/lab/codediff/list?limit=50')
      .then(({ data }) => setDiffs(data.diffs || []))
      .catch(() => {});
  }, []);

  const selected = diffs.find((d) => d.id === selectedId);

  return (
    <div data-testid="lab-codediff" className="grid md:grid-cols-2 gap-3">
      {/* LIST */}
      <div className="space-y-1.5">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
          {diffs.length} diffs
        </div>
        {diffs.length === 0 ? (
          <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
            No code diffs yet. Approve a plan in the Planner tab and click &quot;generate code diff&quot; to create one.
          </div>
        ) : (
          diffs.map((d) => (
            <button
              key={d.id}
              data-testid={`lab-diff-${d.id}`}
              onClick={() => setSelectedId(d.id)}
              className={`w-full text-left border p-2.5 transition-colors ${
                d.id === selectedId
                  ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                  : 'border-[var(--border-default)] hover:border-cyan-sys'
              }`}
            >
              <div className="text-[11px] text-[var(--txt-primary)] truncate">
                {d.summary || `Diff ${d.id.slice(0, 8)}`}
              </div>
              <div className="text-[9px] text-[var(--txt-secondary)] mt-0.5">
                {(d.created_at || '').slice(0, 19).replace('T', ' ')}Z ·
                plan={d.plan_id?.slice(0, 8)} ·
                {(d.diffs || []).length} file(s)
              </div>
            </button>
          ))
        )}
      </div>

      {/* DETAIL */}
      <div className="border border-[var(--border-default)] p-3 min-h-[400px]">
        {!selected ? (
          <div className="text-[11px] text-[var(--txt-secondary)]">Select a diff to view contents.</div>
        ) : (
          <div className="space-y-3 text-[11px]">
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// summary</div>
              <div className="text-[var(--txt-primary)]">{selected.summary}</div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// meta</div>
              <div className="text-[10px] font-mono text-[var(--txt-secondary)]">
                plan_id: {selected.plan_id} · status: {selected.status} ·
                by: {selected.created_by}
              </div>
              {(selected.grounded_files || []).length > 0 && (
                <div className="text-[10px] text-buy font-mono border-l-2 border-buy pl-2">
                  grounded on {selected.grounded_files.length} existing file(s):
                  {' '}
                  {selected.grounded_files.map((g) => (
                    <span key={g.path} className="mr-2">
                      {g.path}{g.truncated ? '⚠' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {(selected.diffs || []).map((d, i) => (
              <div key={i} className="border border-[var(--border-default)]">
                <div className="px-2 py-1 bg-[var(--bg-panel-lighter)] flex items-center justify-between text-[10px]">
                  <div className="flex items-center gap-2">
                    <span className={`uppercase tracking-[0.2em] ${
                      d.action === 'create' ? 'text-buy' : 'text-cyan-sys'
                    }`}>{d.action}</span>
                    <span className="font-mono text-[var(--txt-primary)]">{d.file}</span>
                  </div>
                  <span className="text-[var(--txt-muted)] font-mono">{d.language}</span>
                </div>
                {d.rationale && (
                  <div className="px-2 py-1 text-[10px] text-[var(--txt-secondary)] border-b border-[var(--border-default)]">
                    {d.rationale}
                  </div>
                )}
                <pre
                  data-testid={`lab-diff-content-${i}`}
                  className="p-2 text-[10px] font-mono text-[var(--txt-primary)] whitespace-pre overflow-x-auto max-h-[400px]"
                >
                  {applyDiffColors(d.content || '')}
                </pre>
              </div>
            ))}

            <div className="text-[10px] text-[var(--txt-secondary)] pt-2 border-t border-[var(--border-default)]">
              ⚠ These diffs are NOT applied to the live codebase. Phase 51.D
              (Preview Builder) will provide a sandboxed apply + test workflow.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Lightly colorize unified-diff lines. Plain text otherwise. */
function applyDiffColors(text) {
  if (!text.includes('@@') && !text.startsWith('+') && !text.startsWith('-')) {
    return text;
  }
  return text.split('\n').map((line, i) => {
    let color = 'inherit';
    if (line.startsWith('+') && !line.startsWith('+++')) color = '#5DDA8C';      // buy green
    else if (line.startsWith('-') && !line.startsWith('---')) color = '#FF6B6B'; // sell red
    else if (line.startsWith('@@')) color = '#62D1FF';                            // cyan-sys
    return <span key={i} style={{ color, display: 'block' }}>{line}</span>;
  });
}
